import { z } from "zod";
import { RubricDimensionSchema } from "./primitives.js";
import { EvidenceReferencesSchema } from "./coach-evidence.js";
/**
 * A PRACTICE FOCUS AND WHAT HAPPENED TO IT (coach plan step 9, C08): the
 * durable thread from advice to later independent use. A focus is one thing
 * the learner is working on, with a skill behind it; its events say who
 * suggested it and how, whether the learner chose it in their own words,
 * declined it or replaced it, which exercise attempts were made under it,
 * what the learner said about using it elsewhere, and what a later finished
 * engagement showed when code looked. Each of those is a different event, so
 * memory can say "you chose this", "you tried it here", "you said you used
 * it at work" and "this completed engagement shows it" without inference,
 * and never promote one of them into another.
 *
 * Nothing here changes a reading. A review is code's reading of admissible
 * evidence against the focus's skill; its verdict is descriptive, and a
 * single observed use is described as one, never as a trend.
 */
const Slug = z.string().regex(/^[a-z][a-z0-9-]{1,63}$/);
const noDigits = (text) => !/\d/.test(text);
const Learnerfacing = (max) => z.string().min(1).max(max).refine(noDigits, { message: "learner-facing focus text carries no digits" });
export const FocusOriginSchema = z.enum([
    /** The learner named it, in their own words. */
    "LEARNER_CHOSEN",
    /** Code proposed it from the record's shape or the plan's next step. */
    "PLAN",
    /** The coach proposed it in conversation and the learner took it. */
    "COACH_SUGGESTED",
    /** Migrated from a notes list the coach's model used to write: a historical suggestion the system made. */
    "LEGACY_NOTES",
]);
export const FocusStatusSchema = z.enum(["SUGGESTED", "CHOSEN", "DECLINED", "REPLACED", "DONE"]);
export const ReviewVerdictSchema = z.enum([
    /** The later engagement gave no moment where the skill was exercised: not a failed attempt. */
    "NO_OPPORTUNITY",
    /** Every exercised moment read strong. */
    "SUPPORTED",
    /** Exercised moments read both ways. */
    "MIXED",
    /** Every exercised moment read low. */
    "CONTRADICTED",
]);
/**
 * The closing sentence of a MIXED review's authored note, which points the
 * learner at the review's quotes (2026-10-12). Shared so a surface that shows
 * the note without its references (the weekly email) can leave out exactly
 * this sentence rather than send the learner looking for quotes it lacks.
 */
export const FOCUS_REVIEW_QUOTES_SENTENCE = "The quotes say which.";
export const SelfReportContextSchema = z.enum(["WORK", "ENGAGEMENT", "ELSEWHERE"]);
const eventBase = { seq: z.number().int().min(1), at: z.iso.datetime() };
export const FocusEventSchema = z.discriminatedUnion("type", [
    z.strictObject({ type: z.literal("SUGGESTED"), ...eventBase, dimension: RubricDimensionSchema, text: Learnerfacing(300), origin: FocusOriginSchema, brief_id: Slug.nullable(), reason: z.string().min(1).max(300) }),
    /** The learner's own words choosing it, verified to be theirs by the route; null when chosen by a control. */
    z.strictObject({ type: z.literal("CHOSEN"), ...eventBase, statement: z.string().min(1).max(600).nullable() }),
    z.strictObject({ type: z.literal("DECLINED"), ...eventBase }),
    z.strictObject({ type: z.literal("REPLACED"), ...eventBase, by_focus_id: z.string().min(1) }),
    z.strictObject({ type: z.literal("ATTEMPT_LINKED"), ...eventBase, exercise_id: z.string().min(1), attempt_index: z.number().int().min(1), finished: z.boolean() }),
    /** What the learner said about using it elsewhere: their words, labelled self-report, never evidence. */
    z.strictObject({ type: z.literal("SELF_REPORTED"), ...eventBase, text: z.string().min(1).max(600), context: SelfReportContextSchema }),
    /** Code's review of one later finished engagement against the focus's skill. */
    z.strictObject({
        type: z.literal("REVIEWED"), ...eventBase,
        session_id: z.string().min(1), engagement: z.string().min(1), finished_on: z.string().nullable(),
        verdict: ReviewVerdictSchema,
        /** Moments where the skill was exercised. */
        opportunities: z.number().int().min(0),
        references: EvidenceReferencesSchema,
        /** One or two authored sentences: what supports the observation, its limit, any contradiction. */
        note: z.string().min(1).max(600),
    }),
    z.strictObject({ type: z.literal("DONE"), ...eventBase }),
]);
export const FocusStateSchema = z.strictObject({
    dimension: RubricDimensionSchema,
    text: z.string().min(1),
    origin: FocusOriginSchema,
    brief_id: Slug.nullable(),
    status: FocusStatusSchema,
    chosen_statement: z.string().nullable(),
    attempts: z.array(z.strictObject({ exercise_id: z.string(), attempt_index: z.number().int(), finished: z.boolean() })),
    self_reports: z.array(z.strictObject({ text: z.string(), context: SelfReportContextSchema, at: z.iso.datetime() })),
    reviews: z.array(z.strictObject({ session_id: z.string(), engagement: z.string(), finished_on: z.string().nullable(), verdict: ReviewVerdictSchema, opportunities: z.number().int(), references: EvidenceReferencesSchema, note: z.string(), at: z.iso.datetime() })),
    replaced_by: z.string().nullable(),
    last_seq: z.number().int().min(0),
});
export const FocusViewSchema = z.strictObject({
    focusId: z.string().min(1),
    state: FocusStateSchema,
    /** The evidence kinds present, in the plan's words, so a surface can say what stands behind the focus without inference. */
    standing: z.strictObject({ chosen: z.boolean(), tried: z.boolean(), selfReported: z.boolean(), shownInEngagement: z.boolean(), acrossSituations: z.boolean() }),
});
//# sourceMappingURL=coach-focus.js.map